import { DIFF_STYLE, DIFF_LEGEND_ORDER, type ChangeClass, type DiffResult } from '@drawing2d/core';
import { t, type Lang } from '../i18n.js';

/**
 * The legend, which is also the filter.
 *
 * One control per change class rather than a legend beside a filter, because two
 * lists of the same six things drift: the day someone adds a class to one and
 * not the other, the drawing shows a mark the legend does not explain. Here the
 * row a reviewer reads is the row they click.
 *
 * Every row states its class four ways over — the specimen stroke, the badge
 * silhouette, the line type **in words**, and the label — so the claim that
 * colour is redundant can be checked on the legend itself. The words are the
 * part that survives a greyscale print and a screen reader; the specimen is the
 * part that matches what is on the drawing. Both are drawn from `DIFF_STYLE`,
 * the same table the overlay draws from, so a tuning change cannot make the
 * legend lie.
 */

export function DiffLegend({
  diff,
  lang,
  hidden,
  onToggle,
  monochrome,
  onMonochrome,
}: {
  diff: DiffResult;
  lang: Lang;
  hidden: ReadonlySet<ChangeClass>;
  onToggle: (changeClass: ChangeClass) => void;
  monochrome: boolean;
  onMonochrome: (on: boolean) => void;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);

  return (
    <section className="panel diff-legend">
      <h2>{s('legendTitle')}</h2>
      <p className="note">{s('legendHint')}</p>
      <ul className="legend-rows">
        {DIFF_LEGEND_ORDER.map((changeClass) => {
          const style = DIFF_STYLE[changeClass];
          const off = hidden.has(changeClass);
          return (
            <li key={changeClass}>
              <button
                type="button"
                className={`legend-row${off ? ' off' : ''}`}
                data-change-class={changeClass}
                data-shown={off ? 'no' : 'yes'}
                aria-pressed={!off}
                onClick={() => onToggle(changeClass)}
              >
                <svg className="specimen" viewBox="0 0 62 16" aria-hidden="true">
                  <line
                    x1="1"
                    y1="8"
                    x2="41"
                    y2="8"
                    stroke={monochrome ? 'var(--ink)' : style.colour}
                    strokeWidth={style.strokeWidth}
                    strokeDasharray={style.dasharray === '' ? undefined : style.dasharray}
                    strokeLinecap={style.linecap}
                  />
                  <Badge
                    shape={style.badge}
                    colour={monochrome ? 'var(--ink)' : style.colour}
                    cx={52}
                    cy={8}
                    r={5}
                  />
                </svg>
                <span className="legend-text">
                  <b>{style.label[lang]}</b>
                  <small>
                    {s('patternLabel')}: {style.patternName[lang]}
                  </small>
                </span>
                <span className="legend-count" data-count={diff.summary.byClass[changeClass]}>
                  {diff.summary.byClass[changeClass]}
                </span>
                {off && <span className="legend-off">{s('hiddenNow')}</span>}
              </button>
            </li>
          );
        })}
      </ul>

      <label className="toggle mono-toggle">
        <input
          type="checkbox"
          checked={monochrome}
          onChange={(e) => onMonochrome(e.target.checked)}
        />
        {s('monochrome')}
      </label>
      <p className="note">{s('monochromeHint')}</p>
    </section>
  );
}

/** The silhouette, which is the cue that survives a zoom where a glyph cannot. */
function Badge({
  shape,
  colour,
  cx,
  cy,
  r,
}: {
  shape: (typeof DIFF_STYLE)[ChangeClass]['badge'];
  colour: string;
  cx: number;
  cy: number;
  r: number;
}): JSX.Element | null {
  if (shape === 'none') return null;
  if (shape === 'circle') {
    return <circle cx={cx} cy={cy} r={r} fill="none" stroke={colour} strokeWidth={1} />;
  }
  if (shape === 'square') {
    return (
      <rect
        x={cx - r}
        y={cy - r}
        width={2 * r}
        height={2 * r}
        fill="none"
        stroke={colour}
        strokeWidth={1}
      />
    );
  }
  return (
    <polygon
      points={`${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`}
      fill="none"
      stroke={colour}
      strokeWidth={1}
    />
  );
}
